<template>
  <div class="max-w-4xl mx-auto space-y-3 py-3 mb-24">
    <!-- Preset Selector -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Result PDF Template</h2>
      <p class="text-sm text-gray-500 mb-2">Choose a preset or customize how student result PDFs look when downloaded.</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div
          v-for="preset in presets"
          :key="preset.key"
          class="border rounded-lg p-2.5 cursor-pointer transition-all hover:shadow-md"
          :class="config.preset === preset.key ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'border-gray-200'"
          @click="applyPreset(preset)"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="font-semibold text-sm text-gray-800">{{ preset.name }}</span>
            <i v-if="config.preset === preset.key" class="pi pi-check-circle text-primary"></i>
          </div>
          <p class="text-xs text-gray-500">{{ preset.description }}</p>
        </div>
      </div>
    </div>

    <!-- Header / Logo -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Header & Logo</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Logo Position</label>
          <Select
            v-model="config.logo_position"
            :options="logoPositionOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            @change="markDirty"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Logo Height (px)</label>
          <Slider v-model="config.logo_height" :min="20" :max="150" class="w-full mt-3" @change="markDirty" />
          <span class="text-xs text-gray-400">{{ config.logo_height }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Report Title</label>
          <InputText v-model="config.report_title" class="w-full" placeholder="Student Result Report" @input="markDirty" />
        </div>
      </div>
      <div class="flex flex-wrap gap-3 mt-2">
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_school_name" @change="markDirty" />
          <span class="text-sm text-gray-600">Show school name</span>
        </div>
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_school_address" @change="markDirty" />
          <span class="text-sm text-gray-600">Show school address</span>
        </div>
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_report_title" @change="markDirty" />
          <span class="text-sm text-gray-600">Show report title</span>
        </div>
      </div>
    </div>

    <!-- Table / Font Settings -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Table & Font</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Body Font Size</label>
          <Slider v-model="config.font_size" :min="6" :max="16" class="w-full mt-3" @change="markDirty" />
          <span class="text-xs text-gray-400">{{ config.font_size }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Header Font Size</label>
          <Slider v-model="config.header_font_size" :min="6" :max="14" class="w-full mt-3" @change="markDirty" />
          <span class="text-xs text-gray-400">{{ config.header_font_size }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Row Padding</label>
          <Slider v-model="config.row_padding" :min="1" :max="12" class="w-full mt-3" @change="markDirty" />
          <span class="text-xs text-gray-400">{{ config.row_padding }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Subject Column Width</label>
          <Slider v-model="config.subject_column_width" :min="80" :max="400" class="w-full mt-3" @change="markDirty" />
          <span class="text-xs text-gray-400">{{ config.subject_column_width }}px</span>
        </div>
      </div>
      <div class="flex flex-wrap gap-3 mt-2">
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_borders" @change="markDirty" />
          <span class="text-sm text-gray-600">Show table borders</span>
        </div>
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.striped_rows" @change="markDirty" />
          <span class="text-sm text-gray-600">Striped rows</span>
        </div>
      </div>
    </div>

    <!-- Page Layout -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Page Layout</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Page Size</label>
          <Select
            v-model="config.page_size"
            :options="pageSizeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            @change="markDirty"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Orientation</label>
          <Select
            v-model="config.page_orientation"
            :options="orientationOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            @change="markDirty"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Page Margin</label>
          <Slider v-model="config.page_margin" :min="0" :max="40" class="w-full mt-3" @change="markDirty" />
          <span class="text-xs text-gray-400">{{ config.page_margin }}mm</span>
        </div>
      </div>
    </div>

    <!-- Section Visibility -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Sections</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Student Information</span>
          <InputSwitch v-model="config.show_student_info" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Summary</span>
          <InputSwitch v-model="config.show_summary" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Grading Key</span>
          <InputSwitch v-model="config.show_grading_key" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Attendance</span>
          <InputSwitch v-model="config.show_attendance" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Remarks & Feedback</span>
          <InputSwitch v-model="config.show_remarks" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Footer</span>
          <InputSwitch v-model="config.show_footer" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Signature Lines</span>
          <InputSwitch v-model="config.show_signature_lines" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Watermark</span>
          <InputSwitch v-model="config.show_watermark" @change="markDirty" />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600">Next Term Begins Date</span>
          <InputSwitch v-model="config.show_next_term_date" @change="markDirty" />
        </div>
      </div>

      <!-- Conditional fields -->
      <div class="mt-2 space-y-3">
        <div v-if="config.show_student_info" data-testid="result-pdf-student-fields">
          <label for="result-pdf-student-fields" class="block text-sm font-medium text-surface-600 mb-1">
            Extra student details <span class="text-xs text-surface-400">(printed under the name and class rows)</span>
          </label>
          <MultiSelect
            v-model="selectedStudentFields"
            input-id="result-pdf-student-fields"
            :options="documentFields"
            option-label="label"
            option-value="key"
            display="chip"
            filter
            placeholder="e.g. State of Origin, Blood Group"
            class="w-full"
            :invalid="!!studentFieldsError"
            @change="markDirty"
          />
          <small v-if="studentFieldsError" class="text-red-500 block mt-0.5" data-testid="result-pdf-student-fields-error">{{ studentFieldsError }}</small>
          <small v-else class="text-surface-500 block mt-0.5">Includes your school's custom student fields.</small>
        </div>
        <div v-if="config.show_footer">
          <label class="block text-sm font-medium text-gray-600 mb-1">Custom Footer Text <span class="text-xs text-gray-400">(leave blank for default)</span></label>
          <InputText v-model="config.footer_text" class="w-full" placeholder="Powered by Gradeverse" @input="markDirty" />
        </div>
        <div v-if="config.show_watermark">
          <label class="block text-sm font-medium text-gray-600 mb-1">Watermark Text</label>
          <InputText v-model="config.watermark_text" class="w-full" placeholder="CONFIDENTIAL" @input="markDirty" />
        </div>
        <div v-if="config.show_signature_lines">
          <label class="block text-sm font-medium text-gray-600 mb-1">Signature Labels <span class="text-xs text-gray-400">(press Enter to add, e.g. Principal)</span></label>
          <AutoComplete
            v-model="config.signature_labels"
            multiple
            :typeahead="false"
            class="w-full"
            @update:model-value="markDirty"
          />
        </div>
      </div>
    </div>

    <!-- Save Button -->
    <div class="flex justify-end sticky bottom-4">
      <Button
        label="Save Configuration"
        icon="pi pi-check"
        class="!bg-primary !text-white shadow-lg"
        :loading="isSaving"
        :disabled="!isDirty"
        size="small"
        @click="saveConfig"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';

import { useUpdatePdfResultConfigMutation } from '@/types/vue-apollo-helpers';
import { useFormErrors } from '@/composables/useFormErrors';
import { useStudentDocumentFields } from '@/composables/queries/useStudentDocumentFields';
import { listFieldError, pruneDocumentFields } from '@/utils/studentDocumentFields';
import { GET_PDF_RESULT_CONFIG, GET_PDF_RESULT_PRESETS } from '@/graphql';
import type { PdfResultConfig, PdfResultPreset } from '@/types/composition-functions';

const toast = useToast();
const isSaving = ref(false);
const isDirty = ref(false);

const config = reactive<PdfResultConfig>({
  preset: 'classic',
  logo_position: 'center',
  logo_height: 70,
  show_school_name: true,
  show_school_address: true,
  show_report_title: true,
  report_title: 'Student Result Report',
  subject_column_width: 180,
  font_size: 10,
  header_font_size: 9,
  row_padding: 4,
  show_borders: true,
  striped_rows: false,
  alternate_row_color: '#f9f9f9',
  show_student_info: true,
  student_info_fields: [],
  show_summary: true,
  show_grading_key: true,
  show_attendance: true,
  show_remarks: true,
  show_footer: true,
  footer_text: null,
  page_size: 'A4',
  page_orientation: 'portrait',
  page_margin: 15,
  show_signature_lines: false,
  signature_labels: ['Class Teacher', 'Head Teacher'],
  show_watermark: false,
  watermark_text: 'CONFIDENTIAL',
  show_next_term_date: true,
});

const presets = ref<PdfResultPreset[]>([]);

const logoPositionOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const pageSizeOptions = [
  { label: 'A4', value: 'A4' },
  { label: 'Letter', value: 'Letter' },
];

const orientationOptions = [
  { label: 'Portrait', value: 'portrait' },
  { label: 'Landscape', value: 'landscape' },
];

// Load current config
const { onResult: onConfigResult } = useQuery<{ pdfResultConfig: PdfResultConfig }>(GET_PDF_RESULT_CONFIG);
onConfigResult((result) => {
  if (result.data?.pdfResultConfig) {
    Object.assign(config, result.data.pdfResultConfig);
  }
});

// Load presets
const { onResult: onPresetsResult } = useQuery<{ pdfResultPresets: PdfResultPreset[] }>(GET_PDF_RESULT_PRESETS);
onPresetsResult((result) => {
  if (result.data?.pdfResultPresets) {
    presets.value = result.data.pdfResultPresets;
  }
});

const { mutate: updateConfig } = useUpdatePdfResultConfigMutation();
const { fields: documentFields } = useStudentDocumentFields();
const { serverErrors, handleServerErrors, clearErrors } = useFormErrors();
const studentFieldsError = computed(() => listFieldError(serverErrors.value, 'student_info_fields'));

// PrimeVue's MultiSelect needs a plain array; the config field is nullable.
const selectedStudentFields = computed({
  get: () => (config.student_info_fields ?? []).filter((key): key is string => typeof key === 'string'),
  set: (keys: string[]) => { config.student_info_fields = keys; },
});

function markDirty() {
  isDirty.value = true;
}

function applyPreset(preset: PdfResultPreset) {
  Object.assign(config, preset.config);
  isDirty.value = true;
}

async function saveConfig() {
  try {
    isSaving.value = true;
    clearErrors();
    const input: Record<string, unknown> = {
      ...config,
      // A retired custom field would make the whole save fail; drop it.
      student_info_fields: pruneDocumentFields(selectedStudentFields.value, documentFields.value),
    };
    delete input.__typename;
    await updateConfig({ input });
    isDirty.value = false;
    toast.add({ severity: 'success', summary: 'Saved', detail: 'PDF configuration updated.', life: 3000 });
  } catch (error) {
    handleServerErrors(error);
    if (studentFieldsError.value) return;
    toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Failed to save configuration.', life: 3000 });
  } finally {
    isSaving.value = false;
  }
}
</script>
